import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  ChangedChip,
  CurrentValuesBox,
  MAX_REMARK_LENGTH,
  RemarkField,
  SectionLabel,
  ViewHeader,
} from "./EditDialogShared";
import { PrimaryFormLoadingActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { useToast } from "../../Hooks/useToast";
import { useBatteryInfo } from "../../Pages/DigitalTwinV3/DigitalTwinContext";
import { updateDigitalTwinDetails } from "../../Api/DigitalTwin";
import { getUnlinkedIotList, iotSearchSuggestions } from "../../Api/IoTApi";
import { translateErrorMessage } from "../../Helper/Common/FieldTranslator";
import CODE from "../../Static/Constants/StatusCodes";

const AUTOCOMPLETE_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    fontSize: "14px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    padding: "4px 8px !important",
    "& fieldset": { borderColor: "#E6E6E6", borderWidth: "1px" },
    "&:hover fieldset": { borderColor: "#6D72F6", borderWidth: "1px" },
    "&.Mui-focused fieldset": { borderColor: "#6D72F6", borderWidth: "1px" },
    "& input::placeholder": { color: "#A4B2C1", opacity: 1, fontSize: "14px" },
  },
};

export default function ChangeIotDeviceView({ onBack, onSuccess }) {
  const { openNotification } = useToast();
  const { batteryID, batteryInfo } = useBatteryInfo();

  // The device being replaced, from the real-time info the page already loaded -
  // deviceID is the IMEI. Kept on screen while the user searches so the two
  // IMEIs can be compared digit by digit before the change is confirmed, and
  // refreshed with the rest of the context once the change succeeds.
  const currentDeviceRows = [
    { label: "IMEI", value: batteryInfo?.deviceID },
    { label: "SIM Number", value: batteryInfo?.simNumber },
    { label: "MSISDN", value: batteryInfo?.msisdn },
  ];

  const [inputValue, setInputValue]       = useState("");
  const [suggestions, setSuggestions]     = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);

  // selectedDevice: { imei, simNumber, msisdn } | null
  const [selectedDevice, setSelectedDevice]   = useState(null);
  const [deviceLoading, setDeviceLoading]     = useState(false);
  const [deviceError, setDeviceError]         = useState(null);

  const [remark, setRemark]     = useState("");
  const [errors, setErrors]     = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const debounceRef = useRef(null);

  // Fetch IMEI suggestions as user types (partial match)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!inputValue || inputValue.length < 2 || window.Cypress) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    debounceRef.current = setTimeout(async () => {
      setSuggestLoading(true);
      try {
        const res = await iotSearchSuggestions(inputValue, "imei", 10);
        if (cancelled) return;
        if (res?.responseStatus?.code === CODE.SUCCESS) {
          setSuggestions(res.response?.data ?? []);
        } else {
          setSuggestions([]);
        }
      } finally {
        if (!cancelled) setSuggestLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
  }, [inputValue]);

  // When user picks an IMEI from suggestions, fetch the full device record
  const handleSelectImei = async (imei) => {
    if (!imei) return;
    setDeviceError(null);
    setDeviceLoading(true);
    setSuggestions([]);
    setInputValue("");
    try {
      const res = await getUnlinkedIotList({
        filters: { imei: [imei] },
        pageNumber: 1,
        rowsPerPage: 5,
      });

      if (res?.responseStatus?.code === CODE.SUCCESS) {
        const devices = res.response?.data ?? [];
        const device = devices.find((d) => d.imei === imei) ?? devices[0];
        if (device) {
          setSelectedDevice({
            imei:      device.imei      ?? imei,
            simNumber: device.simNumber ?? null,
            msisdn:    device.msisdn    ?? null,
            iotStatus: device.iotStatus ?? null,
          });
        } else {
          setDeviceError("Device not found in unmapped inventory.");
        }
      } else {
        setDeviceError("Could not retrieve device details. Please try again.");
      }
    } catch {
      setDeviceError("Could not retrieve device details. Please try again.");
    } finally {
      setDeviceLoading(false);
    }
  };

  const handleClearDevice = () => {
    setSelectedDevice(null);
    setDeviceError(null);
    setErrors((prev) => ({ ...prev, device: undefined }));
  };

  const handleSubmit = async () => {
    const errs = {};
    if (!selectedDevice) errs.device = "Please select an IOT device";
    if (!remark.trim()) errs.remark = true;
    if (remark.length > MAX_REMARK_LENGTH) errs.remarkCount = true;

    setErrors(errs);
    if (Object.keys(errs).length) return;

    setIsLoading(true);
    try {
      const response = await updateDigitalTwinDetails(batteryID, {
        imei:      selectedDevice.imei,
        simNumber: selectedDevice.simNumber,
        msisdn:    selectedDevice.msisdn,
        remarks:   remark,
      });

      if (response?.responseStatus?.code !== 200) {
        throw new Error(response?.responseStatus?.msg);
      }

      await onSuccess("IOT Device Updated!");
    } catch (error) {
      const msg = translateErrorMessage(error?.message);
      openNotification({ text: msg, icon: "toast-error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <ViewHeader title="Change IOT Device" onBack={onBack} />

      <CurrentValuesBox label="Current IOT Device" rows={currentDeviceRows} />

      <SectionLabel>New IOT Device</SectionLabel>

      {/* Search input */}
      {!selectedDevice && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography
              variant="fw400s14c000000"
              sx={{ lineHeight: "24px", color: "#494949" }}
            >
              Search by IMEI
            </Typography>
            <Typography
              variant="fw400s14c000000"
              sx={{ fontWeight: 700, lineHeight: "24px", color: "#B50500" }}
            >
              &nbsp;*
            </Typography>
          </Box>
          <Autocomplete
            freeSolo
            disableClearable
            options={suggestions}
            inputValue={inputValue}
            loading={suggestLoading}
            onInputChange={(_, value) => setInputValue(value)}
            onChange={(_, value) => {
              if (value && typeof value === "string") handleSelectImei(value);
            }}
            filterOptions={(x) => x}
            slotProps={{
              listbox: { sx: { fontSize: "14px" } },
            }}
            PaperComponent={(props) => (
              <Paper
                {...props}
                sx={{
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  "& .MuiAutocomplete-option": { fontSize: "14px" },
                }}
              />
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type IMEI to search"
                sx={AUTOCOMPLETE_INPUT_SX}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: suggestLoading ? (
                    <CircularProgress size={16} sx={{ color: "#6D72F6" }} />
                  ) : null,
                }}
              />
            )}
          />
          {errors.device && (
            <Typography sx={{ fontSize: "10px", color: "#930500", mt: 0.5 }}>
              {errors.device}
            </Typography>
          )}
        </Box>
      )}

      {/* Loading state after IMEI selection */}
      {deviceLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} sx={{ color: "#6D72F6" }} />
        </Box>
      )}

      {/* Error fetching device */}
      {deviceError && !deviceLoading && (
        <Typography sx={{ fontSize: "13px", color: "#930500", mb: 2 }}>
          {deviceError}
        </Typography>
      )}

      {/* Selected device card */}
      {selectedDevice && !deviceLoading && (
        <Box
          sx={{
            mb: 2,
            border: "1px solid #E6E6E6",
            borderRadius: "8px",
            bgcolor: "#F9F9F9",
            p: "12px 16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="fw500s14c494949"
                sx={{ fontSize: "13px", color: "#494949", fontWeight: 600 }}
              >
                Selected Device
              </Typography>
              <ChangedChip label="New" />
            </Box>
            <IconButton
              size="small"
              onClick={handleClearDevice}
              sx={{ p: 0.5, color: "#9E9E9E", "&:hover": { color: "#6D72F6" } }}
            >
              <CloseIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1, borderColor: "#F0F0F0" }} />
          {[
            { label: "IMEI",       value: selectedDevice.imei },
            // "N/A" to match CurrentValuesBox six rows above: two spellings of
            // "missing" in one view read as a distinction that does not exist.
            { label: "SIM Number", value: selectedDevice.simNumber ?? "N/A" },
            { label: "MSISDN",     value: selectedDevice.msisdn    ?? "N/A" },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.75,
              }}
            >
              <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>{label}</Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#2E2E2E" }}>
                {value}
              </Typography>
            </Box>
          ))}
          {selectedDevice.iotStatus === "under_testing" && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: "12px" }}>
              This IOT device is currently under testing. End testing before assigning it to a vehicle.
            </Alert>
          )}
        </Box>
      )}

      <RemarkField
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        error={errors.remark}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <PrimaryFormLoadingActionButton
          disabled={isLoading || !selectedDevice || selectedDevice?.iotStatus === "under_testing"}
          onClick={handleSubmit}
          loading={isLoading}
          sx={{ borderRadius: "4px", px: 3 }}
        >
          Confirm Change
        </PrimaryFormLoadingActionButton>
      </Box>
    </Box>
  );
}
